import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDBA } from '@/contexts/DBAContext';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { MobileSheetActions, MobileSheetContent } from '@/components/ui/mobile-sheet';
import { ResponsiveRecordCard, ResponsiveRecordList } from '@/components/ui/responsive-record-list';
import { Plus, Search, Mail, Phone, MapPin, ArrowUpDown, Eye, UsersRound } from 'lucide-react';
import { HighVisibilityEmptyState, HighVisibilityPageHeader } from '@/components/ui/high-visibility-mobile';
import { formatPhone, mapsUrl } from '@/lib/formatters';

export default function CustomersPage() {
  const navigate = useNavigate();
  const { dbas, selectedDBA, getDBAName, getDBAColor } = useDBA();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [error, setError] = useState('');

  const fetchCustomers = useCallback(async () => {
    try {
      const params = {};
      if (selectedDBA) params.dba_id = selectedDBA;
      if (search) params.search = search;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (sortBy !== 'created_at') params.sort_by = sortBy;
      params.sort_dir = sortDir;
      const { data } = await api.get('/customers', { params });
      setCustomers(data);
    } catch { return; } finally {
      setLoading(false);
    }
  }, [selectedDBA, search, sourceFilter, sortBy, sortDir]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    const f = new FormData(e.target);
    const dbaCheckboxes = f.getAll('dba_ids');
    const payload = {
      name: f.get('name') || [f.get('first_name'), f.get('last_name')].filter(Boolean).join(' '),
      first_name: f.get('first_name') || '',
      last_name: f.get('last_name') || '',
      email: f.get('email'),
      phone: f.get('phone'),
      address: f.get('address'),
      dba_ids: dbaCheckboxes,
      tags: f.get('tags') ? f.get('tags').split(',').map(t => t.trim()) : [],
      source: f.get('source'),
    };
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
      } else {
        await api.post('/customers', payload);
      }
      setShowAdd(false);
      setEditing(null);
      fetchCustomers();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const openEdit = (c, e) => {
    e?.stopPropagation();
    setEditing(c);
    setShowAdd(true);
  };

  const openCustomer = (customer) => {
    if (window.matchMedia('(max-width: 639px)').matches) {
      setSelectedCustomer(customer);
      return;
    }
    navigate(`/customers/${customer.id}`);
  };

  const toggleSort = () => {
    if (sortBy === 'name') {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy('name');
      setSortDir('asc');
    }
  };

  return (
    <div className="-mx-3 space-y-5 bg-[#eef7ff] px-0 pb-8 pt-0 animate-fade-in sm:mx-0 sm:bg-transparent sm:px-0 sm:pt-0 lg:space-y-6">
      <HighVisibilityPageHeader
        eyebrow="Customer command"
        title="Customers"
        subtitle="Call, map, edit, and open customer history fast."
        countLabel={`${customers.length} customers`}
        icon={UsersRound}
        tone="violet"
        testId="customers-high-visibility-header"
        titleTestId="customers-title"
        actions={<Button className="h-14 w-full rounded-2xl bg-white px-5 font-black text-violet-700 hover:bg-violet-50 sm:w-auto" onClick={() => { setEditing(null); setShowAdd(true); }} data-testid="add-customer-button"><Plus className="w-5 h-5 mr-2" /> Add Customer</Button>}
      />

      <div className="mx-3 grid gap-3 rounded-[1.5rem] border border-blue-100 bg-white p-3 shadow-sm sm:mx-0 sm:rounded-md lg:grid-cols-[1fr_160px_auto]" data-testid="customer-filter-panel">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by name, email, phone, address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-14 rounded-2xl border-blue-100 pl-9 text-base font-semibold sm:h-11 sm:rounded-sm sm:text-sm"
            data-testid="customer-search-input"
          />
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="h-14 rounded-2xl border border-blue-100 bg-blue-50 px-3 text-base font-black text-blue-800 sm:h-11 sm:rounded-sm sm:bg-white sm:text-sm sm:font-normal"
          data-testid="customer-source-filter"
        >
          <option value="all">All Sources</option>
          <option value="manual">Manual</option>
          <option value="website">Website</option>
          <option value="referral">Referral</option>
          <option value="lead_conversion">Lead Conversion</option>
        </select>
        <Button variant="outline" onClick={toggleSort} className="h-14 rounded-2xl border-blue-100 bg-orange-50 font-black text-orange-700 hover:bg-orange-100 sm:h-11 sm:rounded-sm" data-testid="customer-sort-toggle">
          <ArrowUpDown className="w-4 h-4" />
          {sortBy === 'name' ? `Name ${sortDir === 'asc' ? 'A-Z' : 'Z-A'}` : 'Sort'}
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-zinc-200 rounded-md animate-pulse" />)}</div>
      ) : customers.length === 0 ? (
        <div className="mx-3 sm:mx-0"><HighVisibilityEmptyState icon={UsersRound} title="No customers found" subtitle="Try clearing filters or add a customer." testId="customers-empty-state" /></div>
      ) : (
        <ResponsiveRecordList className="mx-3 sm:mx-0" testId="customers-list">
          {customers.map((c) => (
            <ResponsiveRecordCard
              key={c.id}
              testId={`customer-row-${c.id}`}
              openTestId={`customer-open-${c.id}`}
              title={c.name}
              subtitle={<><span>{c.email || 'No email'}</span>{c.phone && <span> · {formatPhone(c.phone)}</span>}{c.address && <span> · {c.address}</span>}</>}
              onOpen={() => openCustomer(c)}
              badge={<div className="flex gap-1">{c.dba_ids?.map(id => <span key={id} className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: getDBAColor(id) }} title={getDBAName(id)} />)}</div>}
              meta={<span>{c.source || 'manual'}</span>}
              actions={<>{c.phone && <a href={`tel:${c.phone}`} onClick={(event) => event.stopPropagation()} className="inline-flex min-h-12 items-center justify-center gap-1 rounded-2xl bg-sky-500 px-3 font-black text-white sm:min-h-8 sm:rounded-sm sm:bg-white sm:text-zinc-700 sm:ring-1 sm:ring-zinc-200" data-testid={`customer-row-call-link-${c.id}`} aria-label="Call customer"><Phone className="h-4 w-4" />Call</a>}{c.address && <a href={mapsUrl(c.address)} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} className="inline-flex min-h-12 items-center justify-center gap-1 rounded-2xl bg-indigo-500 px-3 font-black text-white sm:min-h-8 sm:rounded-sm sm:bg-white sm:text-zinc-700 sm:ring-1 sm:ring-zinc-200" data-testid={`customer-row-map-link-${c.id}`} aria-label="Open customer address in Google Maps"><MapPin className="h-4 w-4" />Map</a>}<button type="button" onClick={(e) => openEdit(c, e)} className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-violet-600 px-3 font-black text-white sm:min-h-8 sm:rounded-sm sm:bg-white sm:text-zinc-700 sm:ring-1 sm:ring-zinc-200" data-testid={`edit-customer-${c.id}`}>Edit</button></>}
            />
          ))}
        </ResponsiveRecordList>
      )}

      <Dialog open={showAdd} onOpenChange={(open) => { setShowAdd(open); if (!open) setEditing(null); }}>
        <DialogContent className="max-h-[92vh] w-[calc(100vw-1rem)] max-w-lg overflow-y-auto sm:w-full">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Customer' : 'New Customer'}</DialogTitle>
            <DialogDescription>{editing ? 'Update customer information.' : 'Add a new customer record.'}</DialogDescription>
          </DialogHeader>
          {error && <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</p>}
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>First Name</Label>
                <Input name="first_name" defaultValue={editing?.first_name || ''} data-testid="customer-form-first-name" />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input name="last_name" defaultValue={editing?.last_name || ''} data-testid="customer-form-last-name" />
              </div>
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editing?.name || ''} data-testid="customer-form-name" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Email</Label>
                <Input name="email" type="email" defaultValue={editing?.email || ''} data-testid="customer-form-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input name="phone" defaultValue={editing?.phone || ''} data-testid="customer-form-phone" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input name="address" defaultValue={editing?.address || ''} data-testid="customer-form-address" />
            </div>
            <div>
              <Label>Associated Businesses</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {dbas.map(d => (
                  <label key={d.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      name="dba_ids"
                      value={d.id}
                      defaultChecked={editing?.dba_ids?.includes(d.id)}
                      className="rounded border-zinc-300"
                    />
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    {d.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Tags (comma separated)</Label>
                <Input name="tags" defaultValue={editing?.tags?.join(', ') || ''} data-testid="customer-form-tags" />
              </div>
              <div>
                <Label>Source</Label>
                <select name="source" defaultValue={editing?.source || 'manual'} className="w-full h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm" data-testid="customer-form-source">
                  <option value="manual">Manual</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="lead_conversion">Lead Conversion</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowAdd(false); setEditing(null); }}>Cancel</Button>
              <Button type="submit" data-testid="customer-form-submit">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedCustomer} onOpenChange={(open) => { if (!open) setSelectedCustomer(null); }}>
        <MobileSheetContent className="border-0 bg-[#eef7ff] sm:!max-w-lg" data-testid="customer-mobile-detail-sheet">
          {selectedCustomer && (
            <div className="space-y-4">
              <DialogHeader className="rounded-[1.5rem] bg-gradient-to-br from-[#7C3AED] via-[#2563EB] to-[#0EA5E9] p-4 text-white">
                <DialogTitle className="text-3xl font-black font-heading" data-testid="customer-sheet-title">{selectedCustomer.name}</DialogTitle>
                <DialogDescription className="font-semibold text-blue-50">Customer quick view</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 gap-3 text-sm" data-testid="customer-sheet-fields">
                <SheetField label="Email" value={selectedCustomer.email || '-'} testId="customer-sheet-email" />
                <SheetField label="Phone" value={formatPhone(selectedCustomer.phone) || '-'} testId="customer-sheet-phone" />
                <SheetField label="Address" value={selectedCustomer.address || '-'} testId="customer-sheet-address" />
                <SheetField label="Source" value={selectedCustomer.source || 'manual'} testId="customer-sheet-source" />
              </div>
              <div className="flex flex-wrap gap-2" data-testid="customer-sheet-dbas">
                {selectedCustomer.dba_ids?.map(id => <Badge key={id} variant="secondary" className="text-xs"><span className="mr-1 h-2 w-2 rounded-full" style={{ backgroundColor: getDBAColor(id) }} />{getDBAName(id)}</Badge>)}
              </div>
              <MobileSheetActions testId="customer-sheet-actions">
                {selectedCustomer.phone && <a href={`tel:${selectedCustomer.phone}`} className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-sky-500 px-3 text-base font-black text-white" data-testid="customer-sheet-call-button">Call Customer</a>}
                {selectedCustomer.address && <a href={mapsUrl(selectedCustomer.address)} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-indigo-500 px-3 text-base font-black text-white" data-testid="customer-sheet-map-button">Open Map</a>}
                <Button type="button" className="h-14 rounded-2xl bg-blue-600 text-base font-black" onClick={() => navigate(`/customers/${selectedCustomer.id}`)} data-testid="customer-sheet-full-profile-button"><Eye className="mr-2 h-5 w-5" /> Full Profile</Button>
                <Button type="button" variant="outline" className="h-14 rounded-2xl border-violet-100 bg-violet-50 text-base font-black text-violet-700" onClick={(event) => openEdit(selectedCustomer, event)} data-testid="customer-sheet-edit-button">Edit Customer</Button>
              </MobileSheetActions>
            </div>
          )}
        </MobileSheetContent>
      </Dialog>
    </div>
  );
}

function SheetField({ label, value, testId }) {
  return <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm" data-testid={testId}><p className="text-[10px] font-black uppercase tracking-wider text-blue-700">{label}</p><p className="mt-1 break-words text-base font-black text-zinc-900">{value}</p></div>;
}
